<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { IconDefinition as DuotoneIconDefinition } from '@fortawesome/duotone-regular-svg-icons'
import { notifyError } from '@shared/errors'

export type GIconDefinition = IconDefinition | DuotoneIconDefinition | undefined

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const {
  color,
  name: deprecatedName,
  icon: explicitIcon,
  svg,
  size: providedSize = 'sm',
  gradientColorName,
  viewBox: explicitViewBox,
} = defineProps<{
  name?: string
  icon?: GIconDefinition | string
  /**
  * SVG markup, imported directly: `import icon from '~/…/icon.svg?svgicon'`.
  * Renders the SVG inline — statically analyzable and tree-shaken, with no
  * runtime lookup. The preferred way to render a custom (non-FontAwesome) icon.
  * `?svgicon` processes the file exactly as the old sprite did, so it renders
  * identically; use `?raw` for the untouched source. For an already-imported
  * icon carried through a wrapper's `icon` prop, pass it to `icon` instead.
  **/
  svg?: string
  prefix?: string
  color?: string
  size?: string | IconSize
  /**
  * expects a css variable name, e.g. `--gradient-color`
  **/
  gradientColorName?: string
  viewBox?: string
}>()

const isSvgMarkup = (value: unknown): value is string =>
  typeof value === 'string' && value.startsWith('<svg')

const svgMarkup = $computed(() => svg ?? (isSvgMarkup(explicitIcon) ? explicitIcon : undefined))
const icon = $computed(() => svgMarkup ? undefined : explicitIcon as IconDefinition)

// Render the `svg` prop as the root `<svg class="g-icon">` itself (not wrapped),
// so the output matches every other icon. We lift the source's `viewBox` onto
// that root, keep its remaining root attributes (e.g. `fill="none"`) on an inner
// `<g>` — mirroring the sprite `<symbol>` boundary so `.g-icon`'s currentColor
// fill/stroke don't override the icon's own defaults — and inline the rest.
const parsedSvg = $computed(() => {
  if (!svgMarkup) return undefined

  const open = svgMarkup.match(/<svg\b([^>]*)>/i)?.[1] ?? ''
  const inner = svgMarkup
    .replace(/^[\s\S]*?<svg\b[^>]*>/i, '')
    .replace(/<\/svg\s*>\s*$/i, '')

  const rootAttrs: Record<string, string> = {}
  for (const [, key, value] of open.matchAll(/([\w:-]+)\s*=\s*"([^"]*)"/g))
    rootAttrs[key] = value

  const { viewBox, preserveAspectRatio } = rootAttrs
  const { class: rootClass, width, height, xmlns, viewBox: _vb, preserveAspectRatio: _par, ...groupAttrs } = rootAttrs

  // The `?svgicon` plugin tags multicolor illustrations with `g-illustration`
  // on the root `<svg>`; lift it onto our `.g-icon` so its fills/strokes win.
  const illustration = /\bg-illustration\b/.test(rootClass ?? '')

  return {
    viewBox: explicitViewBox || viewBox,
    preserveAspectRatio,
    groupAttrs,
    inner,
    illustration,
  }
})

const originalSize = $computed(() => {
  switch (providedSize) {
    case '30px': return 'xl'
    case '24px': return 'lg'
    case '20px': return 'md'
    case '16px': return 'sm'
    case '12px': return 'xs'
  }
  return providedSize
})

const dimension = $computed(() => {
  if (originalSize?.endsWith('px') || originalSize?.endsWith('rem') || originalSize?.endsWith('em'))
    return originalSize
})

const sizeName = $computed(() => {
  if (!dimension) return originalSize
})

const attrs = $computed(() => {
  return {
    class: [
      sizeName && `g-icon-${sizeName}`,
      parsedSvg?.illustration && 'g-illustration',
      color && !color.startsWith('#') && `text-${color}`,
    ],
    style: [
      color?.startsWith('#') && { color },
      dimension && { height: dimension, width: dimension },
    ],
  }
})

const gradientMaskUrl = $computed(() => {
  // Only calculate if we have both an icon object and a gradient
  if (!icon || !gradientColorName) return ''

  const iconData = icon.icon
  if (!iconData) return ''

  // FontAwesome icon array structure: [width, height, ligatures, unicode, svgPathData]
  const width = iconData[0]
  const height = iconData[1]
  const pathData = iconData[4]

  let pathHtml = ''
  // Standard icons return a string. Duotone icons return an array of strings.
  if (typeof pathData === 'string') {
    pathHtml = `<path d="${pathData}" fill="currentColor" />`
  }
  else if (Array.isArray(pathData)) {
    pathHtml = pathData.map(p => `<path d="${p}" fill="currentColor" />`).join('')
  }

  // Build the raw SVG and safely encode it for the CSS url()
  const rawSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${pathHtml}</svg>`
  return `url('data:image/svg+xml;utf8,${encodeURIComponent(rawSvg)}')`
})

onMounted(() => {
  const el = getCurrentInstance()?.vnode?.el as Element | undefined

  if (deprecatedName || (typeof explicitIcon === 'string' && !isSvgMarkup(explicitIcon))) {
    notifyError(new Error(`GIcon received an unresolvable icon name: "${explicitIcon}"`), {
      tags: { component: 'GIcon' },
      extra: {
        icon: deprecatedName || explicitIcon,
        location: el?.parentElement?.outerHTML?.slice(0, 300),
      },
    })
  }

  if (import.meta.env.DEV && !import.meta.env.VITEST && !import.meta.env.VITE_DISABLE_ICON_WARNINGS) {
    if ([explicitIcon, svg].filter(Boolean).length > 1)
      console.error('Pass only one of `icon` or `svg` to GIcon', el)

    if (color)
      console.warn(`Please avoid using \`color\`, instead use \`class="text-${color}"\``, el)

    if (providedSize?.includes('px') && sizeName)
      console.warn('Please migrate away from px sizes, use sm, md, lg', el)
  }
})
</script>

<template>
  <div
    v-if="icon && gradientColorName"
    class="g-icon g-icon-gradient-mask"
    v-bind="attrs"
    :style="[
      {
        '--mask-image-url': gradientMaskUrl,
        '--mask-gradient-color': gradientColorName
      }
    ]"
  />
  <FontAwesomeIcon
    v-else-if="icon"
    :icon="icon"
    class="g-icon"
    v-bind="attrs"
  />
  <svg
    v-else-if="parsedSvg"
    aria-hidden="true"
    class="g-icon"
    :viewBox="parsedSvg.viewBox"
    :preserveAspectRatio="parsedSvg.preserveAspectRatio"
    v-bind="attrs"
  >
    <title v-if="$attrs.title">{{ $attrs.title }}</title>
    <g v-bind="parsedSvg.groupAttrs" v-html="parsedSvg.inner"/>
  </svg>
</template>

<style lang="scss">
.g-icon {
  fill: currentcolor;
  max-width: initial;
  stroke: currentcolor;

  &.no-stroke {
    stroke: none;
  }
}

.g-icon-gradient-mask {
  background: var(--mask-gradient-color);
  display: inline-block; /* Ensure it behaves like an icon in layout */

  /* Apply the encoded SVG as the mask */
  mask-image: var(--mask-image-url);
  mask-position: center;
  mask-repeat: no-repeat;

  /* Size and position the mask to fit the bounds of the div */
  mask-size: contain;
}

.g-icon.g-illustration {
  fill: initial;
  stroke: initial;
}
</style>
